import React, { useState, useEffect, useRef } from 'react';
import { Sun, MessageCircle, CalendarDays, Briefcase, BookOpen, Layers, ClipboardList, Sparkles, BookMarked } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './PreparePage.css';

const PREP_TYPES = [
  'HR Screening Call', 'HR Interview', 'Technical Interview',
  'Technical Exam / Assessment', 'Final Interview', 'Offer Discussion',
  'General Preparation', 'Other',
];

const DIFF_COLORS = { easy: '#10B981', medium: '#F59E0B', hard: '#EF4444' };
const DIFF_BG = { easy: '#F0FDF4', medium: '#FFFBEB', hard: '#FFF1F2' };

// ── Guide Renderer ─────────────────────────────────────────────────────────
const GuideView = ({ guide, onExport }) => {
  const PRIORITY_COLOR = { high: '#EF4444', medium: '#F59E0B', low: '#10B981' };

  return (
    <div className="prep-result-wrap">
      <div className="prep-result-toolbar">
        <div>
          <div className="prep-result-type">{guide.prepType}</div>
          <div className="prep-result-title">{guide.title}</div>
        </div>
        <button className="prep-export-btn" onClick={onExport}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Export PDF
        </button>
      </div>
      <div className="prep-export-notice">
        This guide is not saved after leaving this page — export it to keep a copy.
      </div>

      {guide.overview && <div className="prep-overview">{guide.overview}</div>}

      {guide.sections?.map((sec, i) => (
        <div key={i} className="prep-section">
          <div className="prep-section-header">
            <span className="prep-section-icon">{sec.icon}</span>
            <span className="prep-section-title">{sec.title}</span>
            <span className="prep-priority-badge" style={{ color: PRIORITY_COLOR[sec.priority] }}>
              {sec.priority?.toUpperCase()} PRIORITY
            </span>
          </div>
          {sec.topics?.map((t, j) => (
            <div key={j} className="prep-topic">
              <div className="prep-topic-name">{t.topic}</div>
              <div className="prep-topic-body">{t.explanation}</div>
              {t.tips?.length > 0 && (
                <ul className="prep-topic-tips">
                  {t.tips.map((tip, k) => <li key={k}>{tip}</li>)}
                </ul>
              )}
              {t.sampleQuestion && (
                <div className="prep-sample-q">
                  <span className="prep-sample-q-label">Likely question:</span> "{t.sampleQuestion}"
                </div>
              )}
            </div>
          ))}
        </div>
      ))}

      {guide.dayOfTips?.length > 0 && (
        <div className="prep-day-of">
          <div className="prep-day-of-title"><Sun size={14} /> Day-of Tips</div>
          {guide.dayOfTips.map((tip, i) => <div key={i} className="prep-day-of-item">✓ {tip}</div>)}
        </div>
      )}

      {guide.questionsToAsk?.length > 0 && (
        <div className="prep-q-to-ask">
          <div className="prep-q-to-ask-title"><MessageCircle size={14} /> Questions You Should Ask Them</div>
          {guide.questionsToAsk.map((q, i) => <div key={i} className="prep-q-to-ask-item">→ {q}</div>)}
        </div>
      )}
    </div>
  );
};

// ── Flashcard Deck ─────────────────────────────────────────────────────────
const FlashcardDeck = ({ flashcards, onExport }) => {
  const [idx, setIdx] = useState(0);         // position within current deck
  const [flipped, setFlipped] = useState(false);
  const [known, setKnown] = useState(new Set()); // original flashcards[] indices
  const [activeCategory, setActiveCategory] = useState(null);

  const categories = [...new Set(flashcards.map(f => f.category))];
  const deck = activeCategory ? flashcards.filter(f => f.category === activeCategory) : flashcards;
  const card = deck[idx] ?? deck[0];
  const origIdx = card ? flashcards.indexOf(card) : -1;

  const next = () => { setFlipped(false); setTimeout(() => setIdx(i => Math.min(i + 1, deck.length - 1)), 150); };
  const prev = () => { setFlipped(false); setTimeout(() => setIdx(i => Math.max(i - 1, 0)), 150); };
  const markKnown = () => {
    if (origIdx >= 0) setKnown(s => { const n = new Set(s); n.add(origIdx); return n; });
    next();
  };

  return (
    <div className="prep-result-wrap">
      <div className="prep-result-toolbar">
        <div>
          <div className="prep-result-type">Flashcard Deck</div>
          <div className="prep-result-title">{flashcards.length} cards across {categories.length} categories</div>
        </div>
        <button className="prep-export-btn" onClick={onExport}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Export PDF
        </button>
      </div>
      <div className="prep-export-notice">Flashcards are not saved — export to keep them.</div>

      <div className="fc-category-bar">
        <button className={`fc-cat-btn ${!activeCategory ? 'active' : ''}`}
          onClick={() => { setActiveCategory(null); setIdx(0); setFlipped(false); }}>All</button>
        {categories.map(cat => (
          <button key={cat} className={`fc-cat-btn ${activeCategory === cat ? 'active' : ''}`}
            onClick={() => { setActiveCategory(cat); setIdx(0); setFlipped(false); }}>
            {cat}
          </button>
        ))}
      </div>

      <div className="fc-progress">
        <div className="fc-progress-bar">
          <div className="fc-progress-fill" style={{ width: `${(known.size / flashcards.length) * 100}%` }} />
        </div>
        <span className="fc-progress-label">{known.size}/{flashcards.length} known</span>
      </div>

      {card && (
        <>
          <div className="fc-meta">
            <span style={{ background: DIFF_BG[card.difficulty], color: DIFF_COLORS[card.difficulty], padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
              {card.difficulty?.toUpperCase()}
            </span>
            <span className="fc-card-num">{idx + 1} / {deck.length}</span>
          </div>

          <div className={`fc-card ${flipped ? 'fc-flipped' : ''}`} onClick={() => setFlipped(f => !f)}>
            <div className="fc-card-inner">
              <div className="fc-card-front">
                <div className="fc-side-label">Question</div>
                <div className="fc-card-text">{card.front}</div>
                <div className="fc-tap-hint">Tap to reveal answer</div>
              </div>
              <div className="fc-card-back">
                <div className="fc-side-label">Answer</div>
                <div className="fc-card-text">{card.back}</div>
              </div>
            </div>
          </div>

          <div className="fc-controls">
            <button className="fc-btn fc-btn-prev" onClick={prev} disabled={idx === 0}>← Prev</button>
            {flipped && (
              <button className="fc-btn fc-btn-known" onClick={markKnown}>✓ Got it</button>
            )}
            <button className="fc-btn fc-btn-next" onClick={next} disabled={idx === deck.length - 1}>Next →</button>
          </div>
        </>
      )}
    </div>
  );
};

// ── Assessment Quiz ────────────────────────────────────────────────────────
const AssessmentQuiz = ({ assessment, onExport }) => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(null);

  const submit = () => {
    let correct = 0, total = 0;
    assessment.questions.forEach(q => {
      if (q.type === 'mcq') {
        total++;
        if (answers[q.id] === q.correctAnswer) correct++;
      }
    });
    setScore({ correct, total });
    setSubmitted(true);
  };

  const TYPE_ICON = { mcq: '○', short: '✏️', reflection: '💭' };

  return (
    <div className="prep-result-wrap">
      <div className="prep-result-toolbar">
        <div>
          <div className="prep-result-type">Mock Assessment</div>
          <div className="prep-result-title">{assessment.title}</div>
        </div>
        <button className="prep-export-btn" onClick={onExport}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Export PDF
        </button>
      </div>
      <div className="prep-export-notice">This assessment is not saved — export to keep a copy.</div>

      {assessment.instructions && <div className="prep-overview">{assessment.instructions}</div>}

      {submitted && score && (
        <div className="quiz-score-banner">
          <div className="quiz-score-num">
            {score.correct}/{score.total}
          </div>
          <div className="quiz-score-sub">Multiple-choice correct</div>
          <div className="quiz-score-note">Review open-ended answers against the explanations below.</div>
        </div>
      )}

      <div className="quiz-questions">
        {assessment.questions?.map((q, i) => {
          const answered = answers[q.id];
          const isCorrect = q.type === 'mcq' && submitted && answered === q.correctAnswer;
          const isWrong = q.type === 'mcq' && submitted && answered && answered !== q.correctAnswer;

          return (
            <div key={q.id} className={`quiz-q ${submitted && q.type === 'mcq' ? (isCorrect ? 'quiz-correct' : isWrong ? 'quiz-wrong' : '') : ''}`}>
              <div className="quiz-q-meta">
                <span className="quiz-q-num">{i + 1}</span>
                <span className="quiz-q-type">{TYPE_ICON[q.type]} {q.type}</span>
                <span className="quiz-q-cat">{q.category}</span>
                <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: DIFF_COLORS[q.difficulty] }}>{q.difficulty?.toUpperCase()}</span>
              </div>
              <div className="quiz-q-text">{q.question}</div>

              {q.type === 'mcq' && (
                <div className="quiz-mcq-opts">
                  {q.options?.map((opt, oi) => {
                    const letter = ['A','B','C','D'][oi];
                    const isThis = answers[q.id] === letter;
                    const isCorrectOpt = submitted && letter === q.correctAnswer;
                    return (
                      <label key={oi} className={`quiz-opt ${isThis ? 'quiz-opt-selected' : ''} ${isCorrectOpt ? 'quiz-opt-correct' : ''} ${submitted && isThis && !isCorrectOpt ? 'quiz-opt-wrong' : ''}`}>
                        <input type="radio" name={q.id} value={letter} disabled={submitted}
                          checked={isThis} onChange={() => setAnswers(a => ({ ...a, [q.id]: letter }))} />
                        <span className="quiz-opt-letter">{letter}</span>
                        <span>{opt.replace(/^[A-D]\.\s*/, '')}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {(q.type === 'short' || q.type === 'reflection') && (
                <textarea
                  className="quiz-short-ans"
                  placeholder={q.type === 'reflection' ? 'Reflect and write your thoughts...' : 'Write your answer here...'}
                  value={answers[q.id] || ''}
                  onChange={e => setAnswers(a => ({ ...a, [q.id]: e.target.value }))}
                  rows={3}
                  disabled={submitted}
                />
              )}

              {submitted && q.explanation && (
                <div className="quiz-explanation">
                  {q.explanation}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!submitted ? (
        <button className="quiz-submit-btn" onClick={submit}>Submit Assessment</button>
      ) : (
        <button className="quiz-reset-btn" onClick={() => { setAnswers({}); setSubmitted(false); setScore(null); }}>
          ↺ Retake Assessment
        </button>
      )}
    </div>
  );
};

// ── Main PreparePage ───────────────────────────────────────────────────────
const PreparePage = ({ jobs, events: initialEvents, preselectedEvent, onReady, onUpgrade }) => {
  const { user, refreshUser } = useAuth();
  const [source, setSource] = useState('event');
  const [selectedJobId, setSelectedJobId] = useState('');
  const [selectedEventId, setSelectedEventId] = useState(preselectedEvent?._id || '');
  const [prepType, setPrepType] = useState(preselectedEvent?.type || '');
  const [userNotes, setUserNotes] = useState('');
  const [events, setEvents] = useState(initialEvents || []);
  const [activeTab, setActiveTab] = useState('guide');
  const [guide, setGuide] = useState(null);
  const [flashcards, setFlashcards] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState({ guide: false, flashcards: false, assessment: false, all: false });
  const [error, setError] = useState('');
  const ready = useRef(false);

  useEffect(() => {
    if (!ready.current) {
      api.get('/api/events').then(r => setEvents(r.data.events)).catch(() => {});
      ready.current = true;
    }
  }, []);

  useEffect(() => {
    if (preselectedEvent) {
      setSource('event');
      setSelectedEventId(preselectedEvent._id);
      setPrepType(preselectedEvent.type || '');
    }
  }, [preselectedEvent]);

  const selectedEvent = events.find(e => e._id === selectedEventId);
  const linkedJob = selectedEvent?.job ? jobs.find(j => j._id === (selectedEvent.job._id || selectedEvent.job)) : null;

  const hasLimitedContext = source === 'event' && selectedEvent && !linkedJob && !selectedEvent.notes;

  const buildPayload = () => ({
    jobId: source === 'job' ? selectedJobId : (linkedJob?._id || null),
    eventId: source === 'event' ? selectedEventId : null,
    prepType: prepType || (source === 'event' ? selectedEvent?.type : '') || 'General Preparation',
    userNotes,
  });

  const generate = async (type) => {
    const payload = buildPayload();
    setError('');
    setLoading(l => ({ ...l, [type]: true }));
    try {
      if (type === 'guide' || type === 'all') {
        const r = await api.post('/api/analyze/prepare/guide', payload);
        setGuide(r.data.guide);
        if (type === 'guide') setActiveTab('guide');
      }
      if (type === 'flashcards' || type === 'all') {
        const r = await api.post('/api/analyze/prepare/flashcards', payload);
        setFlashcards(r.data.flashcards);
        if (type === 'flashcards') setActiveTab('flashcards');
      }
      if (type === 'assessment' || type === 'all') {
        const r = await api.post('/api/analyze/prepare/assessment', payload);
        setAssessment(r.data.assessment);
        if (type === 'assessment') setActiveTab('assessment');
      }
      if (type === 'all') setActiveTab('guide');
      refreshUser();
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 402) {
        setError(data?.message || 'Not enough credits.');
      } else {
        setError(data?.message || 'Generation failed. Check your GEMINI_API_KEY.');
      }
    } finally {
      setLoading(l => ({ ...l, [type]: false }));
    }
  };

  const exportGuidePDF = () => {
    if (!guide) return;
    const html = buildGuidePDF(guide);
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  const exportFlashcardsPDF = () => {
    if (!flashcards) return;
    const html = buildFlashcardsPDF(flashcards);
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  const exportAssessmentPDF = () => {
    if (!assessment) return;
    const html = buildAssessmentPDF(assessment);
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  const hasAny = guide || flashcards || assessment;
  const isLoading = Object.values(loading).some(Boolean);

  return (
    <div className="prepare-root">
      <div className="prepare-header">
        <h1 className="prepare-title">Prepare</h1>
        <p className="prepare-sub">AI-generated review guides, flashcards, and mock assessments for your upcoming events</p>
      </div>

      <div className="prepare-layout">
        {/* Left: Config Panel */}
        <div className="prepare-config">
          <div className="prepare-config-inner">
            <div className="pcfg-section">
              <div className="pcfg-label">Prepare from</div>
              <div className="pcfg-source-toggle">
                <button className={`pcfg-src-btn ${source === 'event' ? 'active' : ''}`} onClick={() => setSource('event')}>
                  <CalendarDays size={13} /> A scheduled event
                </button>
                <button className={`pcfg-src-btn ${source === 'job' ? 'active' : ''}`} onClick={() => setSource('job')}>
                  <Briefcase size={13} /> A job application
                </button>
              </div>
            </div>

            {source === 'event' ? (
              <div className="pcfg-section">
                <label className="pcfg-label">Select Event</label>
                <select className="pcfg-select" value={selectedEventId} onChange={e => {
                  const evId = e.target.value;
                  setSelectedEventId(evId);
                  setGuide(null); setFlashcards(null); setAssessment(null);
                  const ev = events.find(ev => ev._id === evId);
                  if (ev?.type) setPrepType(PREP_TYPES.includes(ev.type) ? ev.type : 'General Preparation');
                }}>
                  <option value="">— Choose an event —</option>
                  {events.map(ev => (
                    <option key={ev._id} value={ev._id}>
                      {ev.title} · {new Date(ev.date).toLocaleDateString('en-PH', { month: 'short', day: 'numeric' })}
                    </option>
                  ))}
                </select>
                {selectedEvent && (
                  <div className="pcfg-event-preview">
                    <div className="pcfg-event-title">{selectedEvent.title}</div>
                    <div className="pcfg-event-meta">
                      {new Date(selectedEvent.date).toLocaleDateString('en-PH', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                      {linkedJob && <span> · {linkedJob.company} — {linkedJob.role}</span>}
                    </div>
                  </div>
                )}
                {hasLimitedContext && (
                  <div className="pcfg-warning">
                    ⚠️ This event has no linked job or notes. The guide will be more general. Add details to the event for better results.
                  </div>
                )}
              </div>
            ) : (
              <div className="pcfg-section">
                <label className="pcfg-label">Select Job Application</label>
                <select className="pcfg-select" value={selectedJobId} onChange={e => { setSelectedJobId(e.target.value); setGuide(null); setFlashcards(null); setAssessment(null); }}>
                  <option value="">— Choose a job —</option>
                  {jobs.map(j => <option key={j._id} value={j._id}>{j.company} — {j.role} [{j.status}]</option>)}
                </select>
              </div>
            )}

            <div className="pcfg-section">
              <label className="pcfg-label">Preparing for</label>
              <select className="pcfg-select" value={prepType} onChange={e => setPrepType(e.target.value)}>
                <option value="">— Select interview/assessment type —</option>
                {PREP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div className="pcfg-section">
              <label className="pcfg-label">Additional context <span className="pcfg-optional">(greatly improves quality)</span></label>
              <textarea
                className="pcfg-textarea"
                rows={4}
                placeholder="Share anything that helps: specific topics you know will be covered, your concerns, the interviewer's LinkedIn profile notes, anything mentioned in the job description, your relevant experience for this role..."
                value={userNotes}
                onChange={e => setUserNotes(e.target.value)}
              />
              <div className="pcfg-accuracy-note">
                The more context you provide here, the more tailored and useful your prep materials will be.
              </div>
            </div>

            {error && <div className="pcfg-error">⚠️ {error}</div>}

            {/* Credit info */}
            <div className="pcfg-credit-bar">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
              <span><strong>{user?.creditsRemaining ?? 15}</strong> credits remaining</span>
              {user?.tier !== 'pro' && <button className="pcfg-upgrade-link" onClick={onUpgrade}>Get Pro →</button>}
            </div>

            {/* Generate buttons */}
            <div className="pcfg-generate-all">
              <button
                className="pcfg-btn-all"
                onClick={() => generate('all')}
                disabled={isLoading || (!selectedEventId && !selectedJobId)}
              >
                {loading.all
                  ? <><span className="btn-spinner" /> Generating everything...</>
                  : <><Sparkles size={14} /> Generate All Three <span className="pcfg-credit-cost">6</span></>
                }
              </button>
              <div className="pcfg-or">or generate individually</div>
              <div className="pcfg-individual-btns">
                <button className="pcfg-btn-individual" onClick={() => generate('guide')} disabled={isLoading || (!selectedEventId && !selectedJobId)}>
                  {loading.guide ? <span className="btn-spinner" /> : <BookOpen size={13} />} Guide <span className="pcfg-credit-cost">3</span>
                </button>
                <button className="pcfg-btn-individual" onClick={() => generate('flashcards')} disabled={isLoading || (!selectedEventId && !selectedJobId)}>
                  {loading.flashcards ? <span className="btn-spinner" /> : <Layers size={13} />} Flashcards <span className="pcfg-credit-cost">2</span>
                </button>
                <button className="pcfg-btn-individual" onClick={() => generate('assessment')} disabled={isLoading || (!selectedEventId && !selectedJobId)}>
                  {loading.assessment ? <span className="btn-spinner" /> : <ClipboardList size={13} />} Assessment <span className="pcfg-credit-cost">2</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Results Panel */}
        <div className="prepare-results">
          {!hasAny ? (
            isLoading ? (
              <div className="prepare-generating">
                <div className="prepare-generating-spinner" />
                <div className="prepare-generating-label">
                  {loading.guide ? 'Writing your review guide...' :
                   loading.flashcards ? 'Creating flashcards...' :
                   loading.assessment ? 'Building assessment...' :
                   'Generating all materials...'}
                </div>
                <div className="prepare-generating-sub">This usually takes 10–20 seconds</div>
              </div>
            ) : (
              <div className="prepare-empty">
                <div className="prepare-empty-icon"><BookMarked size={36} strokeWidth={1.5} /></div>
                <h3>Your prep materials will appear here</h3>
                <p>Select a source, fill in the context, and hit Generate. The more details you provide, the better your materials will be.</p>
                <div className="prepare-empty-items">
                  <div className="pei"><strong>Review Guide</strong> — topic-by-topic breakdown with tips and likely questions</div>
                  <div className="pei"><strong>Flashcards</strong> — interactive Q&A cards to drill key concepts</div>
                  <div className="pei"><strong>Mock Assessment</strong> — self-graded quiz with MCQ, open-ended, and reflection questions</div>
                </div>
              </div>
            )
          ) : (
            <div className="prepare-results-inner">
              {isLoading && (
                <div className="prepare-regen-overlay">
                  <div className="prepare-generating-spinner" />
                  <div className="prepare-generating-label">Regenerating...</div>
                </div>
              )}
              <div className="prep-tabs">
                {guide      && <button className={`prep-tab ${activeTab === 'guide'      ? 'active' : ''}`} onClick={() => setActiveTab('guide')}><BookOpen size={13} /> Review Guide</button>}
                {flashcards && <button className={`prep-tab ${activeTab === 'flashcards' ? 'active' : ''}`} onClick={() => setActiveTab('flashcards')}><Layers size={13} /> Flashcards ({flashcards.length})</button>}
                {assessment && <button className={`prep-tab ${activeTab === 'assessment' ? 'active' : ''}`} onClick={() => setActiveTab('assessment')}><ClipboardList size={13} /> Assessment ({assessment.questions?.length}q)</button>}
              </div>
              {activeTab === 'guide'      && guide      && <GuideView guide={guide} onExport={exportGuidePDF} />}
              {activeTab === 'flashcards'&& flashcards  && <FlashcardDeck flashcards={flashcards} onExport={exportFlashcardsPDF} />}
              {activeTab === 'assessment' && assessment  && <AssessmentQuiz assessment={assessment} onExport={exportAssessmentPDF} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── PDF builders ──────────────────────────────────────────────────────────
const pdfBase = (title, body) => `<!DOCTYPE html><html><head><meta charset="utf-8"><title>ApplAI — ${title}</title>
<style>body{font-family:'Segoe UI',Arial,sans-serif;background:#F5F7FA;padding:28px;color:#111827;font-size:13px;line-height:1.6;}
.header{background:linear-gradient(135deg,#7C3AED,#2563EB);color:white;border-radius:10px;padding:20px 24px;margin-bottom:18px;}
h1{margin:0;font-size:20px;}.sub{margin:3px 0 0;opacity:.8;font-size:12px;}
.notice{background:#FFF3CD;border:1px solid #FDE68A;border-radius:8px;padding:9px 13px;font-size:11px;color:#92400E;margin-bottom:16px;}
.footer{margin-top:24px;text-align:center;font-size:11px;color:#9CA3AF;}</style></head><body>
<div class="header"><h1>${title}</h1><p class="sub">Generated by ApplAI · ${new Date().toLocaleDateString('en-PH',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}</p></div>
<div class="notice">⚠️ This material was generated by AI and is not saved in ApplAI after leaving the page. Keep this PDF as your reference.</div>
${body}
<div class="footer">ApplAI · AI-powered job application tracker &amp; interview preparation</div>
</body></html>`;

const buildGuidePDF = (guide) => pdfBase(guide.title, `
<p style="background:#EFF6FF;border:1px solid #BFDBFE;border-radius:8px;padding:12px 14px;margin-bottom:16px;">${guide.overview || ''}</p>
${guide.sections?.map(s => `<div style="margin-bottom:16px;"><h2 style="font-size:14px;margin:0 0 8px;">${s.icon} ${s.title} <span style="font-size:11px;color:#6B7280;">[${s.priority?.toUpperCase()} PRIORITY]</span></h2>${s.topics?.map(t => `<div style="background:white;border:1px solid #E2E6EE;border-radius:8px;padding:11px 13px;margin-bottom:8px;"><strong>${t.topic}</strong><p style="margin:4px 0 6px;">${t.explanation}</p>${t.tips?.length ? '<ul style="margin:0;padding-left:18px;">' + t.tips.map(tip => `<li>${tip}</li>`).join('') + '</ul>' : ''}${t.sampleQuestion ? `<p style="margin:6px 0 0;color:#6B7280;font-style:italic;">Q: "${t.sampleQuestion}"</p>` : ''}</div>`).join('')}</div>`).join('')}
${guide.dayOfTips?.length ? `<div style="background:#F0FDF4;border:1px solid #BBF7D0;border-radius:8px;padding:12px 14px;"><strong>☀️ Day-of Tips</strong><ul style="margin:6px 0 0;padding-left:18px;">${guide.dayOfTips.map(t => `<li>${t}</li>`).join('')}</ul></div>` : ''}
${guide.questionsToAsk?.length ? `<div style="background:#EFF6FF;border:1px solid #BFDBFE;border-radius:8px;padding:12px 14px;margin-top:12px;"><strong>❓ Questions to Ask</strong><ul style="margin:6px 0 0;padding-left:18px;">${guide.questionsToAsk.map(q => `<li>${q}</li>`).join('')}</ul></div>` : ''}`);

const buildFlashcardsPDF = (flashcards) => pdfBase('Flashcard Deck', `
<style>.fc{display:flex;flex-wrap:wrap;gap:10px;} .card{width:calc(50% - 5px);background:white;border:1px solid #E2E6EE;border-radius:8px;padding:12px;box-sizing:border-box;page-break-inside:avoid;} .front{font-weight:600;margin-bottom:6px;} .back{color:#374151;font-size:12px;} .cat{font-size:10px;color:#9CA3AF;text-transform:uppercase;letter-spacing:.04em;margin-bottom:4px;}</style>
<div class="fc">${flashcards.map(f => `<div class="card"><div class="cat">${f.category} · ${f.difficulty}</div><div class="front">Q: ${f.front}</div><div class="back">A: ${f.back}</div></div>`).join('')}</div>`);

const buildAssessmentPDF = (assessment) => pdfBase(assessment.title, `
<p style="color:#6B7280;margin-bottom:16px;">${assessment.instructions || ''}</p>
${assessment.questions?.map((q, i) => `<div style="background:white;border:1px solid #E2E6EE;border-radius:8px;padding:12px 14px;margin-bottom:10px;"><div style="font-size:11px;color:#9CA3AF;margin-bottom:4px;">${i+1}. ${q.type?.toUpperCase()} · ${q.category}</div><div style="font-weight:600;margin-bottom:8px;">${q.question}</div>${q.type==='mcq' ? `<div>${q.options?.map((o,oi) => `<div style="padding:3px 0;">${o}</div>`).join('')}</div><div style="margin-top:6px;color:#2563EB;font-size:12px;">Answer: ${q.correctAnswer}</div>` : `<div style="background:#F9FAFB;border:1px solid #E2E6EE;border-radius:6px;padding:8px;min-height:40px;margin-bottom:6px;font-size:11px;color:#9CA3AF;">[Write your answer here]</div>`}<div style="font-size:11px;color:#6B7280;margin-top:6px;border-top:1px solid #F0F2F6;padding-top:6px;">📖 ${q.explanation}</div></div>`).join('')}`);

export default PreparePage;
